import { cn } from "@btnet/utilities-core";

import type { IShellNavItem } from "../BTShellLayout/BTShellLayout";
import { BTText } from "../BTText/BTText";

interface IHorizontalNavProps {
  navItems: IShellNavItem[];
  selectedKey: string;
  onSelectKey: (key: string) => void;
  className?: string;
  "data-testid": string;
}

/**
 * Bottom nav for the research-preview shell on mobile (ado-286232). Renders
 * the shared `navItems` as equal-width tap targets with an icon above a
 * label. No hamburger/overflow item and no opt-in/opt-out toggle — those are
 * out of scope for this story. See `Layouts/BTShellLayout` for this composed
 * inside its intended shell layout and `Layouts/BTVerticalNav` for the
 * >=md-breakpoint counterpart.
 */
export function BTHorizontalNav({
  navItems,
  selectedKey,
  onSelectKey,
  className,
  "data-testid": testid,
}: IHorizontalNavProps) {
  return (
    <nav
      data-testid={testid}
      aria-label="Primary"
      className={cn(
        "flex w-full shrink-0 border-t border-gray-15 bg-white",
        className,
      )}
    >
      {navItems.map((item) => {
        const isSelected = item.key === selectedKey;
        return (
          <div
            key={item.key}
            role="button"
            tabIndex={0}
            data-testid={`${testid}-item-${item.key}`}
            onClick={() => onSelectKey(item.key)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onSelectKey(item.key);
              }
            }}
            className={cn(
              "relative flex flex-1 cursor-pointer flex-col items-center justify-center gap-1 py-2 text-sm",
              {
                "text-fg-default": isSelected,
                "text-fg-secondary": !isSelected,
              },
            )}
          >
            {item.icon}
            <BTText
              size="normal-sm"
              className={cn({
                "font-semibold": isSelected,
                "font-normal": !isSelected,
              })}
            >
              {item.label}
            </BTText>
            {isSelected && (
              <span
                aria-hidden="true"
                className="absolute bottom-1 h-0.5 w-4 rounded-sm bg-fg-default"
              />
            )}
          </div>
        );
      })}
    </nav>
  );
}
